import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSetBreadcrumbs, type BreadcrumbEntry } from "@/shared/contexts/breadcrumb-context";
import { MAP_URL } from "@/map/config/map-urls";
import type { TLocation } from "@/locations/types/location.ts";
import type { Machine } from "@/machines/types/machine.ts";

interface UseMapBreadcrumbsArgs {
  organizationName?: string;
  organizationId: number | null;
  isOrgLoading: boolean;
  /** Only shown while zoomed into a location (the org view has no location crumb). */
  location: TLocation | null;
  showLocation: boolean;
  machine: Machine | null;
  /**
   * Applies a pick from the location dropdown. MUST be identity-stable:
   * `useSetBreadcrumbs` diffs entries with `JSON.stringify`, which drops
   * functions, so an unstable callback is never re-published and the crumb keeps
   * calling the first render's closure. Wrap it in a ref-backed `useCallback`.
   */
  onSelectLocation: (id: number) => void;
}

/**
 * Publishes the map's breadcrumb trail: Organization › Map › Location › Machine.
 *
 * The org crumb is the standard route-based switcher. The LOCATION crumb is a
 * switcher too, but applies its pick in-page via `onSelect` — the map holds its
 * location in component state, not the URL, so there is no id in the path to
 * swap. The machine crumb is display-only.
 */
export const useMapBreadcrumbs = ({
  organizationName,
  organizationId,
  isOrgLoading,
  location,
  showLocation,
  machine,
  onSelectLocation,
}: UseMapBreadcrumbsArgs) => {
  const { t } = useTranslation("map");

  useSetBreadcrumbs(
    useMemo(() => {
      const crumbs: BreadcrumbEntry[] = [
        {
          path: MAP_URL.ROOT,
          breadcrumb: organizationName || t("breadcrumbs.organization"),
          type: "organizations",
          currentId: organizationId!,
          isLoading: isOrgLoading,
        },
        { path: MAP_URL.ROOT, breadcrumb: t("breadcrumbs.map") },
      ];
      if (showLocation && location)
        crumbs.push({
          path: MAP_URL.ROOT,
          breadcrumb: location.name,
          type: "locations",
          currentId: location.id,
          onSelect: onSelectLocation,
        });
      if (machine) crumbs.push({ path: MAP_URL.ROOT, breadcrumb: machine.name });
      return crumbs;
    }, [
      t,
      organizationName,
      organizationId,
      isOrgLoading,
      showLocation,
      location,
      machine,
      onSelectLocation,
    ]),
  );
};
